import { useSearchParams } from 'react-router-dom';

import { clubSidebar } from '@/config/sidebarConfig.ts';
import { getClubHistoryApi } from '@/lib/api/clubsApi';
import { getPageNumbers } from '@/lib/pagination';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr, formatDateTimeRelative } from '@shared';
import type { ClubHistoryEvent, ClubHistoryEventType } from '@shared/types/entities';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Filter,
  MapPin,
  Trophy,
  UserCheck,
  UserX,
  Users2,
} from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';
import StatCard from '../../components/ui/StatCard';

// ─── Config par type d'événement ────────────────────────────────
const EVENT_CONFIG: Record<
  ClubHistoryEventType,
  {
    label: string;
    labelBadge: string;
    icon: typeof CheckCircle;
    iconBg: string;
    iconColor: string;
    badgeBg: string;
    badgeText: string;
  }
> = {
  INSCRIPTION: {
    label: 'Inscription',
    labelBadge: 'INSCRIPTION',
    icon: Users2,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-700',
  },
  PAIEMENT: {
    label: 'Paiement',
    labelBadge: 'PAIEMENT',
    icon: CreditCard,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-700',
  },
  MEMBRE_REJOINT: {
    label: 'Membre rejoint',
    labelBadge: 'MEMBRE',
    icon: UserCheck,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    badgeBg: 'bg-purple-100',
    badgeText: 'text-purple-700',
  },
  DEMANDE_REJETEE: {
    label: 'Demande rejetée',
    labelBadge: 'REJET',
    icon: UserX,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    badgeBg: 'bg-red-100',
    badgeText: 'text-red-700',
  },
};

// ─── Filtres disponibles ─────────────────────────────────────────
const FILTER_OPTIONS: { value: ClubHistoryEventType | ''; label: string }[] = [
  { value: '', label: 'Tous les événements' },
  { value: 'INSCRIPTION', label: 'Inscriptions' },
  { value: 'PAIEMENT', label: 'Paiements' },
  { value: 'MEMBRE_REJOINT', label: 'Membres rejoints' },
  { value: 'DEMANDE_REJETEE', label: 'Demandes rejetées' },
];

// ─── Helpers ────────────────────────────────────────────────────
function fullName(firstName?: string | null, lastName?: string | null): string {
  return [firstName, lastName].filter(Boolean).join(' ') || '—';
}

function buildDescription(event: ClubHistoryEvent): string {
  switch (event.type) {
    case 'INSCRIPTION':
      return event.team?.competition
        ? `Équipe "${event.team.name}" inscrite au concours "${event.team.competition.name}"`
        : `Équipe "${event.team?.name}" inscrite à un concours`;
    case 'PAIEMENT': {
      const amount = event.amount != null ? ` (${(event.amount / 100).toFixed(2)} €)` : '';
      return event.team?.competition
        ? `Paiement reçu${amount} pour l'équipe "${event.team.name}" — ${event.team.competition.name}`
        : `Paiement reçu${amount} pour l'équipe "${event.team?.name}"`;
    }
    case 'MEMBRE_REJOINT':
      return `${fullName(event.user?.firstName, event.user?.lastName)} a rejoint le club`;
    case 'DEMANDE_REJETEE':
      return `Demande d'adhésion de ${fullName(event.user?.firstName, event.user?.lastName)} rejetée`;
    default:
      return 'Événement inconnu';
  }
}

function buildSubDetail(event: ClubHistoryEvent): string | null {
  if ((event.type === 'INSCRIPTION' || event.type === 'PAIEMENT') && event.team?.competition) {
    const comp = event.team.competition;
    const parts: string[] = [];
    parts.push(formatDateAbbreviatedFr(comp.date));
    if (comp.venue ?? comp.city) parts.push(comp.venue ?? comp.city ?? '');
    return parts.join(' · ');
  }
  if ((event.type === 'MEMBRE_REJOINT' || event.type === 'DEMANDE_REJETEE') && event.user?.email) {
    return event.user.email;
  }
  return null;
}

// ─── Event Row ───────────────────────────────────────────────────
function EventRow({ event, isLast }: { event: ClubHistoryEvent; isLast: boolean }) {
  const cfg = EVENT_CONFIG[event.type];
  const Icon = cfg.icon;
  const description = buildDescription(event);
  const subDetail = buildSubDetail(event);

  return (
    <div className='relative flex gap-4'>
      {/* Timeline icon + connector */}
      <div className='flex flex-col items-center shrink-0'>
        <div className={`w-10 h-10 ${cfg.iconBg} rounded-full flex items-center justify-center z-10`}>
          <Icon className={`w-5 h-5 ${cfg.iconColor}`} />
        </div>
        {!isLast && <div className='w-0.5 bg-slate-200 flex-1 min-h-[20px]' />}
      </div>

      {/* Card */}
      <div className='flex-1 bg-white rounded-xl border border-slate-200 p-4 mb-4 hover:shadow-md transition-shadow'>
        <div className='flex items-start justify-between gap-3'>
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-semibold text-slate-900'>{description}</p>

            {subDetail && (
              <p className='text-xs text-slate-500 mt-1 flex items-center gap-1'>
                {event.type === 'INSCRIPTION' || event.type === 'PAIEMENT' ? (
                  <>
                    <Calendar className='w-3 h-3 shrink-0' />
                    {subDetail.split(' · ')[0]}
                    {subDetail.includes(' · ') && (
                      <>
                        <span className='text-slate-300'>·</span>
                        <MapPin className='w-3 h-3 shrink-0' />
                        {subDetail.split(' · ').slice(1).join(' · ')}
                      </>
                    )}
                  </>
                ) : (
                  subDetail
                )}
              </p>
            )}
          </div>

          <span
            className={`shrink-0 inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.badgeBg} ${cfg.badgeText}`}
          >
            {cfg.labelBadge}
          </span>
        </div>

        <p className='text-xs text-slate-400 mt-2' title={event.date}>
          {formatDateTimeRelative(event.date)}
        </p>
      </div>
    </div>
  );
}

// ─── Skeleton row ────────────────────────────────────────────────
function SkeletonRow({ isLast }: { isLast: boolean }) {
  return (
    <div className='relative flex gap-4'>
      <div className='flex flex-col items-center shrink-0'>
        <div className='w-10 h-10 bg-slate-200 animate-pulse rounded-full' />
        {!isLast && <div className='w-0.5 bg-slate-200 flex-1 min-h-[20px]' />}
      </div>
      <div className='flex-1 bg-white rounded-xl border border-slate-200 p-4 mb-4'>
        <div className='flex items-start justify-between gap-3'>
          <div className='flex-1 space-y-2'>
            <div className='h-4 bg-slate-200 animate-pulse rounded w-3/4' />
            <div className='h-3 bg-slate-100 animate-pulse rounded w-1/2' />
          </div>
          <div className='h-5 w-20 bg-slate-200 animate-pulse rounded-full shrink-0' />
        </div>
        <div className='h-3 bg-slate-100 animate-pulse rounded w-24 mt-2' />
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────
const PAGE_SIZE = 20;

export default function ClubHistory() {
  const clubId = useAuthStore(s => s.user)?.clubId;
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  const [searchParams, setSearchParams] = useSearchParams();
  const typeFilter = (searchParams.get('type') ?? '') as ClubHistoryEventType | '';
  const page = parseInt(searchParams.get('page') ?? '1', 10);

  const setParam = (key: string, value: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  const params = {
    ...(typeFilter && { type: typeFilter }),
    page,
    limit: PAGE_SIZE,
  };

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: queryKeys.clubs.list({ history: true, clubId, ...params }),
    queryFn: () => getClubHistoryApi(clubId!, params),
    enabled: !!clubId && isAuthenticated,
    placeholderData: keepPreviousData,
  });

  const events = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const totalPages = data?.meta?.totalPages ?? 1;
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  // Compteurs globaux (tous événements, hors filtre/page) fournis par l'API.
  const counts = data?.meta?.counts;

  function handleTypeChange(value: ClubHistoryEventType | '') {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (value) next.set('type', value);
        else next.delete('type');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  }

  if (isError) {
    return (
      <DashboardLayout sidebarConfig={clubSidebar}>
        <div className='p-8'>
          <QueryErrorState onRetry={refetch} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebarConfig={clubSidebar}>
      <div className='p-8'>
        <PageHeader title='Historique du club' subtitle='Inscriptions, paiements et évolutions des membres' />

        {/* ─── Stats ─── */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
          <StatCard
            icon={Trophy}
            iconBg='bg-slate-100'
            iconColor='text-slate-600'
            label='Événements totaux'
            value={String(counts?.total ?? 0)}
            subtitle='Toutes catégories'
          />
          <StatCard
            icon={Users2}
            iconBg='bg-blue-100'
            iconColor='text-blue-600'
            label='Inscriptions'
            value={String(counts?.INSCRIPTION ?? 0)}
            subtitle='Total'
          />
          <StatCard
            icon={CreditCard}
            iconBg='bg-emerald-100'
            iconColor='text-emerald-600'
            label='Paiements'
            value={String(counts?.PAIEMENT ?? 0)}
            subtitle='Total'
          />
          <StatCard
            icon={UserCheck}
            iconBg='bg-purple-100'
            iconColor='text-purple-600'
            label='Membres rejoints'
            value={String(counts?.MEMBRE_REJOINT ?? 0)}
            subtitle='Total'
          />
        </div>

        {/* ─── Filtres ─── */}
        <div className='flex items-center gap-3 mb-8 flex-wrap'>
          <Filter className='w-4 h-4 text-slate-400 shrink-0' />
          {FILTER_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => handleTypeChange(opt.value)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                typeFilter === opt.value
                  ? 'bg-emerald-600 text-white shadow-md'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-emerald-400 hover:text-emerald-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
          {isFetching && !isLoading && <span className='text-xs text-slate-400 ml-auto'>Actualisation…</span>}
        </div>

        {/* ─── Timeline ─── */}
        {!clubId ? (
          <div className='flex flex-col items-center justify-center py-16 text-slate-400 gap-3'>
            <Users2 className='w-12 h-12 opacity-30' />
            <p className='text-sm font-medium'>Aucun club associé à votre compte</p>
          </div>
        ) : isLoading ? (
          <div className='space-y-1'>
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonRow key={i} isLast={i === 5} />
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-20 text-slate-400 gap-3'>
            <Trophy className='w-12 h-12 opacity-20' />
            <p className='text-sm font-medium'>Aucun événement{typeFilter ? ' pour ce filtre' : ''}</p>
            {typeFilter && (
              <button onClick={() => handleTypeChange('')} className='text-xs text-emerald-600 hover:underline'>
                Afficher tous les événements
              </button>
            )}
          </div>
        ) : (
          <>
            <div className='space-y-1'>
              {events.map((event, index) => (
                <EventRow key={event.id} event={event} isLast={index === events.length - 1} />
              ))}
            </div>

            {/* ─── Pagination ─── */}
            {total > PAGE_SIZE && (
              <div className='flex items-center justify-between mt-6 pt-4 border-t border-slate-200'>
                <p className='text-sm text-slate-500'>
                  Événements <span className='font-semibold text-slate-700'>{from}</span> à{' '}
                  <span className='font-semibold text-slate-700'>{to}</span> sur{' '}
                  <span className='font-semibold text-slate-700'>{total.toLocaleString('fr-FR')}</span>
                </p>
                {totalPages > 1 && (
                  <div className='flex items-center gap-1'>
                    <button
                      onClick={() => setParam('page', String(Math.max(1, page - 1)))}
                      disabled={page === 1}
                      className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                      aria-label='Page précédente'
                    >
                      <ChevronLeft className='h-4 w-4' />
                    </button>
                    {getPageNumbers(page, totalPages).map((p, i) =>
                      p === '...' ? (
                        <span key={`dots-${i}`} className='px-2 text-slate-400'>
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setParam('page', String(p))}
                          aria-current={p === page ? 'page' : undefined}
                          className={`rounded-lg px-3 py-1 text-sm font-semibold transition ${
                            p === page ? 'bg-emerald-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {p}
                        </button>
                      ),
                    )}
                    <button
                      onClick={() => setParam('page', String(Math.min(totalPages, page + 1)))}
                      disabled={page >= totalPages}
                      className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                      aria-label='Page suivante'
                    >
                      <ChevronRight className='h-4 w-4' />
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
